
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import dayjs from "dayjs";
import ImportHistory from "@/data/models/importHistory";
import Building from "@/data/models/building";

@Component({})
export default class AdminOperation extends Vue {
  @Prop({required: true})
  buildingId: string;

  @Prop({required: true})
  meterId: string;

  exporting: boolean = false;
  exportingDerive: boolean = false;

  datePickerStart: boolean = false;
  datePickerEnd: boolean = false;
  cleanStartDate: string = dayjs().subtract(1, "day").format('YYYY-MM-DD');
  cleanEndDate: string = dayjs().format('YYYY-MM-DD');

  headers = [
    {text: 'Nom du fichier', value: 'filename'},
    {text: 'Type', value: 'type'},
    {text: 'Date', value: 'createat'},
    {text: 'Données', value: 'raw'},
    {text: '', value: 'action', align: 'right'},
  ];
  logs: Array<ImportHistory> = [];

  retrying: { [id: string]: boolean } = {};
  // Contenu affiché dans la fenêtre « Résultat » / « Données »
  preview: { title: string, content: string, loading?: boolean } = null;
  buildings: Array<Building> = [];
  selectedBuilding: string = null;

  @Watch('cleanStartDate')
  onCleanStartDateChange(): void {
    this.datePickerStart = false;
  }

  @Watch('cleanEndDate')
  onCleanEndDateChange(): void {
    this.datePickerEnd = false;
  }

  get deriveInterval(): string {
    return `${dayjs().subtract(366, 'day').format('DD-MM-YYYY')} au ${dayjs().subtract(1, 'day').format('DD-MM-YYYY')}`;
  }

  mounted(): void {
    this.fetchLogsByCsvMappingFilenames();
    this.getBuildingsList();
  }

  async fetchLogsByCsvMappingFilenames(): Promise<void> {
    // Get all CSV mapping filenames
    const filenames = await this.$dao.meters.getStructuresById(this.meterId).then((result) => result.map((item) => item.filename));

    // Get all logs by CSV mapping filenames
    const logs = await Promise.all(filenames.map((filename) => this.$dao.logs.getByFilename(filename)));
    this.logs = logs.flat().sort((a, b) => b.createat.getTime() - a.createat.getTime());
  }

  show(item: ImportHistory, element: string): void {
    if (element === 'results') {
      this.preview = {title: `Résultat — ${item.filename}`, content: JSON.stringify(item.results, null, 2)};
    } else if (element === 'raw') {
      // Contenu brut chargé à la demande : les listes ne le renvoient pas
      const preview = {title: `Données — ${item.filename}`, content: '', loading: true};
      this.preview = preview;
      this.$dao.logs.getRaw(item.id)
          .then((raw) => preview.content = raw)
          .catch(() => this.$dialog.error({text: 'Impossible de charger les données', title: 'Erreur'}))
          .finally(() => preview.loading = false);
    }
  }

  copyPreview(): void {
    navigator.clipboard.writeText(this.preview.content).then(() => this.$dialog.notify.success('Données copiées'));
  }

  retry(id: string): void {
    this.$set(this.retrying, id, true);
    this.$dao.logs.replay(id).then(() => {
      this.fetchLogsByCsvMappingFilenames();
      this.$emit('refresh');
      this.$set(this.retrying, id, false);
    }).catch(() => {
      this.$dialog.error({text: 'Une erreur est survenue', title: 'Erreur'})
      this.$set(this.retrying, id, false);
    });
  }

  handleDownload(): void {
    this.exporting = true;
    // 18month period
    const startDate = dayjs().subtract(18, 'month');
    const endDate = dayjs();
    this.$dao.meters.downloadRawData(this.meterId, startDate, endDate).then((content) => {
      const blob = new Blob([content], {type: 'text/csv'})
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.meterId}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
      this.exporting = false;
    })
  }

  handleDownloadDerive(): void {
    this.exportingDerive = true

    this.$dao.meters.downloadDerive(this.meterId).then((content) => {
      const blob = new Blob([content], {type: 'text/csv'})
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.meterId}_derive.csv`
      link.click()
      URL.revokeObjectURL(link.href)
      this.exportingDerive = false;
    })
  }

  handleDelete(): void {
    let startDate = `${this.cleanStartDate} 00:00:00`;
    let endDate = `${this.cleanEndDate} 23:59:59`;

    this.$dialog
        .confirm({actions: [{text: "Oui", key: true}], text: `Confirmer la suppression des données du compteur du ${startDate} au ${endDate} ?`, title: "Suppression des données ?"})
        .then((result) => {
          if (result) {
            this.$dao.meters.deleteMeterReadingsInterval(this.meterId, startDate, endDate)
                .then(() => {
                  this.$emit('refresh');
                  this.$dialog.confirm({title: "Succès", text: "Les données ont été supprimées avec succès.", actions: ['Fermer']})
                })
                .catch(() => {
                  this.$dialog.error({text: "Une erreur est survenue lors de la suppression des données", title: "Désolé"});
                })
          }
        });
  }

  async getBuildingsList(): Promise<void> {
    this.buildings = this.$store.getters.currentUserBuildings.map((item) => item.building);
    // Get all buildings from all users (admin only), add the client name before the building name
    const allBuildings = await Promise.all((await (this.$dao.clients.getAll())).map((u) => {
      return this.$dao.buildings.getAllByUserId(u.id).then((buildings) => buildings.map((b) => {
        b.name = `${u.nameSurname} - ${b.name}`;
        return b;
      }));
    }))
        .then((results) => results.flat())
        .then((buildings) => {
          // Remove buildings that are already in the list
          return buildings.filter((b) => !this.buildings.find((bb) => bb.id === b.id));
        });

    // Filter buildings not already in the list
    this.buildings = [...this.buildings, ...allBuildings];
  }

  moveMeterToBuilding(): void {
    this.$dao.meters.moveMeterToBuilding(this.meterId, this.selectedBuilding)
        .then(() => this.$dialog.notify.success('Compteur déplacé avec succès'))
        .catch(() => this.$dialog.error({text: 'Une erreur est survenue lors du déplacement du compteur', title: 'Erreur'}));
  }
}
